import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { store } from 'redux/store';
import { BugTracker } from 'Utils/Bugtracker';
import { notify } from 'components/Notifications/HotToastNotifications';
import {
  CosneticsAuthenticationError,
  isCosneticsAuthenticationError
} from '../cosneticsAuthentication';
import {
  rejectCosneticsSession,
  withCosneticsSession
} from 'services/session';
import type { SessionTicket } from 'services/session/SessionCoordinator';

interface DBQ {
  config: AxiosRequestConfig;
  url: string;
  method?: string;
  throwOnAuthError?: boolean;
}

const LEGACY_API_SLOW_REQUEST_THRESHOLD_MS = 3000;
// This client must never install auth interceptors on the global Axios instance:
// that instance also calls Microsoft, Firebase and other third-party APIs.
const cosneticsClient = axios.create();

// const shouldShowNotifications = () => {
//   const allowedUrls = [
//     'http://localhost:3000',
//     'https://test.bips.tech',
//     'https://staging-bips.netlify.app'
//   ];

//   return allowedUrls.includes(window.location.origin);
// };

const handleNotification = (type: 'error' | 'info', message: string) => {
  // if (shouldShowNotifications()) {
  notify[type](message);
  // }
};

const logLegacyRequestTiming = ({
  url,
  method,
  status,
  durationMs
}: {
  url: string;
  method?: string;
  status: number;
  durationMs: number;
}) => {
  if (durationMs < LEGACY_API_SLOW_REQUEST_THRESHOLD_MS) return;

  console.info('[legacy-api] Slow request', {
    method: method ?? 'get',
    url,
    status,
    durationMs
  });
};

const logLegacyRequestFailure = ({
  url,
  method,
  durationMs,
  error
}: {
  url: string;
  method?: string;
  durationMs: number;
  error: AxiosError;
}) => {
  console.error('[legacy-api] Request failed', {
    method: method ?? 'get',
    url,
    durationMs,
    code: error.code,
    status: error.response?.status,
    statusText: error.response?.statusText,
    errorHeader: error.response?.headers?.error,
    message: error.message
  });
};

/**
 * Database Queue
 * @param {object} config
 * @param {string} url
 * @param {string} method
 */
export const dbQueue = async ({
  config,
  url,
  method,
  throwOnAuthError
}: DBQ): Promise<AxiosResponse> =>
  await processQ({ config, url, method, throwOnAuthError });

const withAuthHeader = (headers: AxiosRequestConfig['headers'], token: string) => {
  const result = { ...headers };
  for (const name of Object.keys(result)) {
    if (name.toLowerCase() === 'auth') delete result[name];
  }
  return { ...result, Auth: token };
};

const protectedConfig = (config: AxiosRequestConfig, ticket: SessionTicket): AxiosRequestConfig => {
  const base = new URL(ticket.baseURL);
  const target = new URL(config.url || '', base);
  if (target.origin !== base.origin || !target.pathname.startsWith(base.pathname)) {
    throw new Error('The request URL does not match the active Cosnetics API.');
  }
  return {
    ...config,
    baseURL: ticket.baseURL,
    headers: withAuthHeader(config.headers, ticket.token)
  };
};

const processQ = async ({
  config,
  url,
  method,
  throwOnAuthError
}: DBQ): Promise<AxiosResponse> => {
  const startedAt = Date.now();
  // Password reset uses the one-time token supplied in the reset link, before
  // there is a signed-in user. It must not join or invalidate a shared session.
  const isPasswordReset = /^UserInstanceAPI\.ashx\?action=PASSWORDRESET$/i.test(url);
  const isAuthenticationRequest = /^AuthAPI\.ashx(?:\?|$)/i.test(url);

  const request = async (requestConfig: AxiosRequestConfig, ticket?: SessionTicket) => {
    try {
      const response = await cosneticsClient(requestConfig);
      // Some callers accept non-2xx statuses using validateStatus. Treat those
      // 401s identically to rejected Axios requests, with the captured ticket.
      if (response.status === 401) {
        if (ticket) await rejectCosneticsSession(ticket);
        throw new CosneticsAuthenticationError();
      }
      return response;
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 401) {
        if (ticket) await rejectCosneticsSession(ticket);
        throw new CosneticsAuthenticationError();
      }
      throw error;
    }
  };

  try {
    const response = isAuthenticationRequest || isPasswordReset
      ? await request({
          ...config,
          baseURL: config.baseURL || store.getState().config.baseURL,
          ...(isPasswordReset ? {
            headers: withAuthHeader(config.headers, store.getState().user.auth.token)
          } : {})
        })
      : await withCosneticsSession((ticket) => request(protectedConfig(config, ticket), ticket));

    logLegacyRequestTiming({
      method: config.method ?? method,
      url,
      status: response.status,
      durationMs: Date.now() - startedAt
    });

    if (response.status !== 200) {
      handleNotification(
        response.status === 404 ? 'info' : 'error',
        `${response.statusText}: ${response.headers?.error}`
      );
    }
    return response;
  } catch (error) {
    if (isCosneticsAuthenticationError(error)) {
      if (throwOnAuthError) throw new CosneticsAuthenticationError();
      return {} as AxiosResponse;
    }
    // A temporarily unavailable coordinator prevents requests without logging
    // the user out. The next explicit action/resume can try again; mutations
    // are never replayed automatically.
    if ((error as { code?: string })?.code === 'SESSION_UNAVAILABLE') {
      if (throwOnAuthError) throw error;
      return {} as AxiosResponse;
    }

    const e = error as AxiosError;
    logLegacyRequestFailure({
      method: config.method ?? method,
      url,
      durationMs: Date.now() - startedAt,
      error: e
    });
    handleNotification(
      e?.response?.status === 404 ? 'info' : 'error',
      e?.response?.status === 404
        ? `${e.response.headers?.error}`
        : `${e?.response?.statusText}: ${e?.response?.headers?.error}`
    );
    BugTracker.notify(e);
    return {} as AxiosResponse;
  }
};

export default dbQueue;
